export const MAX_FILE_SIZE_MB = 25;
export const MAX_FILE_SIZE = 2 ** 20 * MAX_FILE_SIZE_MB;
export const THUMBNAIL_MAX_HEIGHT = 400;
export const THUMBNAIL_MAX_WIDTH = 400;
export const IMAGE_MAX_HEIGHT = 1600;
export const IMAGE_MAX_WIDTH = 1600;
export const IMAGE_COMPRESSION = 80;
export const THUMBNAIL_COMPRESSION = 40;

export type FileBeingAdded = {
  name: string;
  size: number;
  width: number;
  height: number;
} & ({ file: undefined; uri: string } | { file: File; uri: undefined });

export enum FileTypeGroupsEnum {
  image = 'image',
  text = 'text',
  document = 'document',
  vector = 'vector',
  email = 'email',
  file = 'file',
  spreadsheet = 'spreadsheet',
  pdf = 'pdf',
  powerpoint = 'powerpoint',
  zip = 'zip',
  svg = 'svg',
  video = 'video',
}

export enum FileTypeExtensions {
  csv = 'csv',
  bmp = 'bmp',
  doc = 'doc',
  docx = 'docx',
  dwg = 'dwg',
  eml = 'eml',
  file = 'file',
  gif = 'gif',
  jpeg = 'jpeg',
  jpg = 'jpg',
  msg = 'msg',
  pdf = 'pdf',
  png = 'png',
  ppt = 'ppt',
  pptx = 'pptx',
  ps = 'ps',
  rar = 'rar',
  rtf = 'rtf',
  svg = 'svg',
  tiff = 'tiff',
  txt = 'txt',
  xls = 'xls',
  xlsm = 'xlsm',
  xlsx = 'xlsx',
  xml = 'xml',
  zip = 'zip',
  heic = 'heic',
  heif = 'heif',
  mov = 'mov',
  mp4 = 'mp4',
  mpeg = 'mpeg',
  webm = 'webm',
}

export interface FileType {
  mime: string;
  name: string;
  group: FileTypeGroupsEnum;
  extension: FileTypeExtensions;
  icon: string;
  uti: string;
}

export const FILE_TYPES: Record<FileTypeExtensions, FileType> = {
  [FileTypeExtensions.bmp]: {
    extension: FileTypeExtensions.bmp,
    group: FileTypeGroupsEnum.image,
    icon: 'image',
    mime: 'image/bmp',
    name: 'BMP Image',
    uti: 'com.microsoft.bmp',
  },
  [FileTypeExtensions.csv]: {
    extension: FileTypeExtensions.csv,
    group: FileTypeGroupsEnum.text,
    icon: 'text',
    mime: 'text/csv',
    name: 'CSV',
    uti: 'public.comma-separated-values-text',
  },
  [FileTypeExtensions.doc]: {
    extension: FileTypeExtensions.doc,
    group: FileTypeGroupsEnum.document,
    icon: 'doc',
    mime: 'application/msword',
    name: 'Microsoft Word',
    uti: 'com.microsoft.word.doc',
  },
  [FileTypeExtensions.docx]: {
    extension: FileTypeExtensions.docx,
    group: FileTypeGroupsEnum.document,
    icon: 'doc',
    mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    name: 'Microsoft Word',
    uti: 'org.openxmlformats.wordprocessingml.document',
  },
  [FileTypeExtensions.dwg]: {
    extension: FileTypeExtensions.dwg,
    group: FileTypeGroupsEnum.vector,
    icon: 'vector',
    mime: 'application/acad',
    name: 'AutoCAD Drawing Database',
    uti: 'com.autodesk.dwg',
  },
  [FileTypeExtensions.eml]: {
    extension: FileTypeExtensions.eml,
    group: FileTypeGroupsEnum.email,
    icon: 'email',
    mime: 'message/rfc822',
    name: 'EML',
    uti: 'com.apple.mail.email',
  },
  [FileTypeExtensions.file]: {
    extension: FileTypeExtensions.file,
    group: FileTypeGroupsEnum.file,
    icon: 'file',
    mime: 'application/octet-stream',
    name: 'Unknown File',
    uti: 'public.data',
  },
  [FileTypeExtensions.gif]: {
    extension: FileTypeExtensions.gif,
    group: FileTypeGroupsEnum.image,
    icon: 'image',
    mime: 'image/gif',
    name: 'GIF Image',
    uti: 'com.compuserve.gif',
  },
  [FileTypeExtensions.jpeg]: {
    extension: FileTypeExtensions.jpeg,
    group: FileTypeGroupsEnum.image,
    icon: 'image',
    mime: 'image/jpeg',
    name: 'JPEG',
    uti: 'public.jpeg',
  },
  // Include jpg to catch badly named jpeg files
  [FileTypeExtensions.jpg]: {
    extension: FileTypeExtensions.jpeg,
    group: FileTypeGroupsEnum.image,
    icon: 'image',
    mime: 'image/jpeg',
    name: 'JPEG',
    uti: 'public.jpeg',
  },
  [FileTypeExtensions.msg]: {
    extension: FileTypeExtensions.msg,
    group: FileTypeGroupsEnum.email,
    icon: 'email',
    mime: 'application/vnd.ms-outlook',
    name: 'MSG',
    uti: 'com.microsoft.outlook.msg',
  },
  [FileTypeExtensions.pdf]: {
    extension: FileTypeExtensions.pdf,
    group: FileTypeGroupsEnum.pdf,
    icon: 'pdf',
    mime: 'application/pdf',
    name: 'PDF',
    uti: 'com.adobe.pdf',
  },
  [FileTypeExtensions.png]: {
    extension: FileTypeExtensions.png,
    group: FileTypeGroupsEnum.image,
    icon: 'image',
    mime: 'image/png',
    name: 'PNG Image',
    uti: 'public.png',
  },
  [FileTypeExtensions.ppt]: {
    extension: FileTypeExtensions.ppt,
    group: FileTypeGroupsEnum.powerpoint,
    icon: 'powerpoint',
    mime: 'application/vnd.ms-powerpoint',
    name: 'Microsoft PowerPoint',
    uti: 'com.microsoft.powerpoint.ppt',
  },
  [FileTypeExtensions.pptx]: {
    extension: FileTypeExtensions.pptx,
    group: FileTypeGroupsEnum.powerpoint,
    icon: 'powerpoint',
    mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    name: 'Microsoft PowerPoint',
    uti: 'org.openxmlformats.presentationml.presentation',
  },
  [FileTypeExtensions.ps]: {
    extension: FileTypeExtensions.ps,
    group: FileTypeGroupsEnum.vector,
    icon: 'vector',
    mime: 'application/postscript',
    name: 'PostScript',
    uti: 'com.adobe.postscript',
  },
  [FileTypeExtensions.rar]: {
    extension: FileTypeExtensions.rar,
    group: FileTypeGroupsEnum.zip,
    icon: 'zip',
    mime: 'application/x-rar-compressed',
    name: 'RAR',
    uti: 'com.rarlab.rar-archive',
  },
  [FileTypeExtensions.rtf]: {
    extension: FileTypeExtensions.rtf,
    group: FileTypeGroupsEnum.text,
    icon: 'text',
    mime: 'text/rtf',
    name: 'RTF',
    uti: 'public.rtf',
  },
  [FileTypeExtensions.svg]: {
    extension: FileTypeExtensions.svg,
    group: FileTypeGroupsEnum.svg,
    icon: 'vector',
    mime: 'image/svg+xml',
    name: 'SVG',
    uti: 'public.svg-image',
  },
  [FileTypeExtensions.tiff]: {
    extension: FileTypeExtensions.tiff,
    group: FileTypeGroupsEnum.image,
    icon: 'image',
    mime: 'image/tiff',
    name: 'TIFF Image',
    uti: 'public.tiff',
  },
  [FileTypeExtensions.txt]: {
    extension: FileTypeExtensions.txt,
    group: FileTypeGroupsEnum.text,
    icon: 'text',
    mime: 'text/plain',
    name: 'TXT',
    uti: 'public.plain-text',
  },
  [FileTypeExtensions.xls]: {
    extension: FileTypeExtensions.xls,
    group: FileTypeGroupsEnum.spreadsheet,
    icon: 'excel',
    mime: 'application/vnd.ms-excel',
    name: 'Microsoft Excel',
    uti: 'com.microsoft.excel.xls',
  },
  [FileTypeExtensions.xlsm]: {
    extension: FileTypeExtensions.xlsm,
    group: FileTypeGroupsEnum.spreadsheet,
    icon: 'excel',
    mime: 'application/vnd.ms-excel.sheet.macroEnabled.12',
    name: 'Microsoft Excel',
    uti: 'org.openxmlformats.spreadsheetml.sheet.macroenabled',
  },
  [FileTypeExtensions.xlsx]: {
    extension: FileTypeExtensions.xlsx,
    group: FileTypeGroupsEnum.spreadsheet,
    icon: 'excel',
    mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    name: 'Microsoft Excel',
    uti: 'org.openxmlformats.spreadsheetml.sheet',
  },
  [FileTypeExtensions.xml]: {
    extension: FileTypeExtensions.xml,
    group: FileTypeGroupsEnum.text,
    icon: 'text',
    mime: 'text/xml',
    name: 'XML',
    uti: 'public.xml',
  },
  [FileTypeExtensions.zip]: {
    extension: FileTypeExtensions.zip,
    group: FileTypeGroupsEnum.zip,
    icon: 'zip',
    mime: 'application/zip',
    name: 'ZIP',
    uti: 'com.pkware.zip-archive',
  },
  [FileTypeExtensions.heic]: {
    extension: FileTypeExtensions.heic,
    group: FileTypeGroupsEnum.image,
    icon: 'image',
    mime: 'image/heic',
    name: 'HEIC Image',
    uti: 'public.heic',
  },
  [FileTypeExtensions.heif]: {
    extension: FileTypeExtensions.heif,
    group: FileTypeGroupsEnum.image,
    icon: 'image',
    mime: 'image/heif',
    name: 'HEIF Image',
    uti: 'public.heif',
  },
  [FileTypeExtensions.mp4]: {
    extension: FileTypeExtensions.mp4,
    group: FileTypeGroupsEnum.video,
    icon: 'video',
    mime: 'video/mp4',
    name: 'MP4 Video',
    uti: 'public.mpeg-4',
  },
  [FileTypeExtensions.mov]: {
    extension: FileTypeExtensions.mov,
    group: FileTypeGroupsEnum.video,
    icon: 'video',
    mime: 'video/quicktime',
    name: 'MOV Video',
    uti: 'com.apple.quicktime-movie',
  },
  [FileTypeExtensions.mpeg]: {
    extension: FileTypeExtensions.mpeg,
    group: FileTypeGroupsEnum.video,
    icon: 'video',
    mime: 'video/mpeg',
    name: 'MPEG Video',
    uti: 'public.mpeg',
  },
  [FileTypeExtensions.webm]: {
    extension: FileTypeExtensions.webm,
    group: FileTypeGroupsEnum.video,
    icon: 'video',
    mime: 'video/webm',
    name: 'WEBM Video',
    uti: 'org.webmproject.webm',
  },
};

export enum FileSourceType {
  FILE,
  IMAGE,
  CAMERA,
}
